import Konva from 'konva';
import { createCableArea, createDrawings, getObjectDefaultMeasurementsByType, isCable, isEcc, isPipe } from './drawings';
import type { ICableModel } from '$lib/models/ICableModel';
import { trenchStateStore } from '$lib/stores/trench-store';
import { get } from 'svelte/store';
import { getCableAreaHeight, getCoverThickness, getTrenchHeight, getTrenchTopWidth } from '$lib/core/map/trench-utils';

let currentStageObject: Konva.Stage | undefined = undefined;
let viewOptions = {
  optionalRulers: false,
  referencePositions: false
};

let stageRect: Konva.RectConfig,
viewRect: Konva.RectConfig,
fullRect: Konva.RectConfig,
gridRect: Konva.RectConfig;

const gridLayer: Konva.Layer = new Konva.Layer({
  listening: false, // Disable mouse events for the grid layer
  draggable: false,
  x: 0,
  y: 0
});

const hintLayer: Konva.Layer = new Konva.Layer();
const trenchLayer: Konva.Layer = new Konva.Layer({
  listening: false, // Disable mouse events for the trench layer
});
const entityLayer: Konva.Layer = new Konva.Layer({
  draggable: false,
  x: 0,
  y: 0
});

const getViewOptions = () => {
  return viewOptions;
}

/**
 * Sets the view options for the grid and rulers. Returns the updated view options.
 * @param optionalRulers Whether to show optional rulers on the grid. More is coming.
 * @returns 
 */
const setViewOptions = ({
  optionalRulers,
  referencePositions
}: {
  optionalRulers?: boolean;
  referencePositions?: boolean;
}) => {
  viewOptions = getViewOptions();
  if(optionalRulers !== undefined) viewOptions.optionalRulers = optionalRulers;
  if(referencePositions !== undefined) viewOptions.referencePositions = referencePositions;
  return viewOptions;
};

const getStageObject = () => {
  return currentStageObject;
}

const getLayers = () => {
  return {
    entityLayer,
    trenchLayer,
    hintLayer
  }
}

/**
 * Rerenders the entire stage. Should be optimized to only rerender the necessary parts. 
 * 
 * @param mode We can force rerendering in different modes. No argument or 'normal' defaults the grid. If we force for export, some hints and rulers are added.
 * 
 */
const rerenderStage = (mode: 'normal' | 'export' = 'normal') => {
  const stageObject = getStageObject();
  const trenchData = get(trenchStateStore);
  if (!stageObject) {
    console.error('Stage object is not defined');
    return;
  }
  
  const width = stageObject.attrs.width;
  const height = stageObject.attrs.height;
  const scaleHint = stageObject.scaleX().toString(); // Should be a getter function.

  clearGrid();

  if(mode === 'export'){
    createGrid(stageObject, width, height, scaleHint, {optionalRulers: true, referencePositions: true}, true);
  }else {
	clearEntities();
    createDrawings(trenchData!);
    createGrid(stageObject, width, height, scaleHint, viewOptions);
  }
  // Is running while measurements are edited in parent component. Make sure it actually rerenders the canvas.
};

/**
	 * Used for calculating with a scalable Stage.
	 * The returned value will calculate relative to zoom/scale of 1.
	 *
	 * @param val - The value to ignore current scale
	 */
const unScale = (val: number, stageObjectProvided?: Konva.Stage) => {
  let stageObject = getStageObject();
  if(!stageObject){
    stageObject = stageObjectProvided;
  }
  if (!stageObject) {
    console.error('Stage object is not defined');
    return val; // Return the value as is if stageObject is not defined
  }
  return val / stageObject!.scaleX();
};

/**
 * Clears the grid and hints for the canvas. More or less static drawings that should never change, except in scaling and canvas position. 
 */
const clearGrid = () => {
  const layers = [gridLayer, hintLayer];
  layers.forEach((layer) => {
    if (layer) {
      layer.clear();
      layer.destroyChildren();
      layer.clipWidth(0);
    }
  })
};

/**
 * Clears the entities from the specified layers. Entities are generally dynamic in nature, and should be recalculated and redrawn for each stage update.
 * @param selectedLayers Optional parameter to clear specific layers. If not provided, it clears all layers.
 */
const clearEntities = (selectedLayers?: Konva.Layer[]) => {
  const layers = selectedLayers || [trenchLayer, entityLayer];
  layers.forEach((layer) => {
    if (layer) {
      layer.clear();
      layer.destroyChildren();
      layer.clipWidth(0);
    }
  });
}

const verticalRulerGroup = new Konva.Group();
const horizontalRulerGroup = new Konva.Group();
const STEP_SIZE = 10; // set a value for the grid step gap.

// Function to add vertical labels
const addVerticalLabel = (
  verticalRulerGroup: Konva.Group,
  yCoordinate: number,
  xRulerPosition: number
) => {
  verticalRulerGroup.add(
    new Konva.Text({
      text: yCoordinate.toString(),
      fontFamily: 'Poppins',
      align: 'right',
      fontSize: unScale(14),
      fontStyle: 'Bold',
      lineHeight: 1.2,
      padding: 8,
      offsetY: unScale(6),
      offsetX: unScale(-45),
      fill: 'black',
      x: xRulerPosition - unScale(40),
      y: yCoordinate - 9
    })
  );
};

const drawVerticalRuler = (yCoordinate: number) => {
  const xRulerPosition = viewRect.x1; // Ruler will be drawn on the left
  addVerticalLabel(verticalRulerGroup, yCoordinate, xRulerPosition);
  // Draw the vertical ruler lines
  verticalRulerGroup.add(
    new Konva.Line({
      x: xRulerPosition,
      y: yCoordinate,
      points: [0, 0, unScale(10), 0], // short line next to each label
      stroke: 'rgba(0, 0, 0, 1)',
      strokeWidth: 2
    })
  );
  // Add the vertical ruler to the hint layer (or a separate dedicated layer)
  hintLayer.add(verticalRulerGroup);
};

/**
	 * Draws the grid and shapes that should be updated along with the canvas.
	 * Todo: Limit area so that the canvas is not fully dynamic.
	 */
const createLabel = (
  gridRect: { x1: number; y1: number; x2: number; y2: number },
  horizontalRulerGroup: Konva.Group,
  xCoordinate: number,
  yCoordinate: number,
  interval: number
) => {
  const stepXCoordinate = gridRect.x1 + interval * STEP_SIZE;
  horizontalRulerGroup.add(
    new Konva.Text({
      text: stepXCoordinate.toString(),
      fontFamily: 'Poppins',
      align: 'center',
      fontSize: unScale(14),
      fontStyle: 'Bold',
      lineHeight: 1.2,
      padding: 8,
      width: unScale(100),
      offsetX: unScale(50),
      fill: 'black',
      x: xCoordinate,
      y: yCoordinate
    })
  );
  horizontalRulerGroup.add(
    new Konva.Line({
      points: [stepXCoordinate, 5, stepXCoordinate, -5],
      stroke: 'rgba(0, 0, 0, 1)',
      strokeWidth: 2
    })
  );
};

const drawHorizontalRuler = ({yPos, xStart, xEnd}: {yPos: number, xStart: number, xEnd: number}) => {
    const markStart = new Konva.Line({
      points: [
        xStart,
        yPos + 5,
        xStart,
        yPos - 5
      ],
      stroke: 'rgba(0,0,255,0.75)'
    })
    const lineObject = new Konva.Line({
      points: [
        xStart,
        yPos,
        xEnd,
        yPos
      ],
      stroke: 'rgba(0,0,255,0.75)',
    });
    const markEnd = new Konva.Line({
      points: [
        xEnd,
        yPos + 5,
        xEnd,
        yPos - 5
      ],
      stroke: 'rgba(0,0,255,0.75)'
    });
    const labelObject = new Konva.Text({
      x: (xStart + xEnd) / 2,
      y: yPos - unScale(20),
      text: `${Math.abs(Math.round(xEnd - xStart))} mm`,
      fontSize: unScale(16),
      fontFamily: 'Poppins',
      align: 'center',
      offsetX: unScale(30)
    })

    hintLayer.add(markStart, markEnd, lineObject, labelObject);
}

const drawVertical = ({xPos, yStart, yEnd}: {xPos: number, yStart: number, yEnd: number}) => {
    const lineObject = new Konva.Line({
      points: [
        xPos,
        yStart,
        xPos,
        yEnd
      ],
      stroke: 'rgba(0,0,255,0.75)'
    })
    hintLayer.add(lineObject);
}

const createGrid = (stageObject: Konva.Stage, width: number, height: number, scaleHint: string, viewOptions: { optionalRulers: boolean, referencePositions: boolean }, createWithoutGrid: boolean = false, trenchData: ICableModel.TrenchData = get(trenchStateStore)!) => {
  // stageObject is of type Konva.Stage, which is the root container for all Konva shapes.
  if (!stageObject) {
    console.log('createGrid: stageObject is not defined');
    return;
  }
  const { optionalRulers, referencePositions } = viewOptions;
  setViewOptions({ optionalRulers, referencePositions });
  currentStageObject = stageObject;

  clearGrid();

  // We divide into layers to separate drawing collections. This allows us to clear, redraw, hide, show, etc. the collections without affecting other drawings.
  const requiredLayers = [gridLayer, hintLayer, trenchLayer, entityLayer];
  requiredLayers.map((layer) => {
    if (!layer.parent) {
      stageObject.add(layer);
    }
  });

  // Origin is always (0,0) for all drawings
  // These rects are for interactivity, e.g. drawing tools.

  // stageRect is a rectangle that represents the original stage size and position. Offsets are dynamically calculated based on the stage position and scale.
  stageRect = {
    x1: 0,
    y1: 0,
    x2: stageObject.attrs.width,
    y2: stageObject.attrs.height,
    offset: {
      x: unScale(stageObject.x()),
      y: unScale(stageObject.y())
    }
  };

  // viewRect is a rectangle that represents the viewable area of the stage.
  viewRect = {
    x1: -stageRect.offset!.x,
    y1: -stageRect.offset!.y,
    x2: unScale(width) - stageRect.offset!.x,
    y2: unScale(height) - stageRect.offset!.y
  };

  // Find the largest rectangle that bounds both the stage and view rect.
  // This is the rect we will draw on.
  fullRect = {
    x1: Math.min(stageRect.x1, viewRect.x1),
    y1: Math.min(stageRect.y1, viewRect.y1),
    x2: Math.max(stageRect.x2, viewRect.x2),
    y2: Math.max(stageRect.y2, viewRect.y2)
  };
  let gridOffset = {
    x: Math.ceil(unScale(stageObject.x()) / STEP_SIZE) * STEP_SIZE,
    y: Math.ceil(unScale(stageObject.y()) / STEP_SIZE) * STEP_SIZE
  };
  gridRect = {
    x1: -gridOffset.x,
    y1: -gridOffset.y,
    x2: unScale(width) - gridOffset.x + STEP_SIZE,
    y2: unScale(height) - gridOffset.y + STEP_SIZE
  };
  let gridFullRect = {
    x1: Math.min(stageRect.x1, gridRect.x1),
    y1: Math.min(stageRect.y1, gridRect.y1),
    x2: Math.max(stageRect.x2, gridRect.x2),
    y2: Math.max(stageRect.y2, gridRect.y2)
  };
  if(createWithoutGrid) return;

   

  const drawGridLines = () => {
    // set clip function to stop leaking lines into non-viewable space.
    gridLayer.clip({
      x: viewRect.x1,
      y: viewRect.y1,
      width: viewRect.x2 - viewRect.x1,
      height: viewRect.y2 - viewRect.y1
    });

    let fullRect = gridFullRect;
    const // find the x & y size of the grid
      xSize = fullRect.x2 - fullRect.x1,
      ySize = fullRect.y2 - fullRect.y1,
      // compute the number of steps required on each axis.
      xSteps = Math.round(xSize / STEP_SIZE),
      ySteps = Math.round(ySize / STEP_SIZE);

    // Draw vertical lines
    const gridLinesVertical = new Konva.Group();
    const gridLinesHorizontal = new Konva.Group();
    const scaleInNumber = parseFloat(scaleHint);

    // Add vertical labels and lines as a ruler
    // Rewrite to add ticks and labels to a group and with proper world coordinates
    for (let i = 0; i <= xSteps; i++) {
      const xCoordinate = fullRect.x1 + i * STEP_SIZE;

      if (xCoordinate % 100 === 0) {
        gridLinesVertical.add(
          new Konva.Line({
            x: fullRect.x1 + i * STEP_SIZE,
            y: fullRect.y1,
            points: [0, 0, 0, ySize],
            stroke: 'rgba(133, 133, 133, 1)',
            strokeWidth: unScale(1)
          })
        );
      } else if (scaleInNumber >= 0.4 && STEP_SIZE % 5 === 0) {
        gridLinesVertical.add(
          new Konva.Line({
            x: fullRect.x1 + i * STEP_SIZE,
            y: fullRect.y1,
            points: [0, 0, 0, ySize],
            stroke: 'rgba(200, 200, 200, 1)',
            strokeWidth: unScale(1)
          })
        );
      }

      // Add labels based on the scale size (intervals adjust with the zoom level)
      if (scaleInNumber > 0.5 && xCoordinate % 100 === 0) {
        createLabel(fullRect, horizontalRulerGroup, xCoordinate, 0, i);
      } else if (scaleInNumber <= 0.5 && scaleInNumber > 0.2 && xCoordinate % 200 === 0) {
        createLabel(fullRect, horizontalRulerGroup, xCoordinate, 0, i);
      } else if (scaleInNumber <= 0.2 && xCoordinate % 500 === 0) {
        createLabel(fullRect, horizontalRulerGroup, xCoordinate, 0, i);
      }
    }
    // Draw Horizontal lines
    // Rewrite to add ticks and labels to a group and with proper world coordinates
    for (let i = 0; i <= ySteps; i++) {
      const yCoordinate = fullRect.y1 + i * STEP_SIZE;
      // Add labels based on the scale size (intervals adjust with the zoom level)
      if (scaleInNumber > 0.5 && yCoordinate % 100 === 0) {
        drawVerticalRuler(yCoordinate);
      } else if (scaleInNumber <= 0.5 && scaleInNumber > 0.2 && yCoordinate % 200 === 0) {
        drawVerticalRuler(yCoordinate);
      } else if (scaleInNumber <= 0.2 && yCoordinate % 500 === 0) {
        drawVerticalRuler(yCoordinate);
      }
      if (yCoordinate % 100 === 0) {
        gridLinesHorizontal.add(
          new Konva.Line({
            x: fullRect.x1,
            y: fullRect.y1 + i * STEP_SIZE,
            points: [0, 0, xSize, 0],
            stroke: 'rgba(133, 133, 133, 1)',
            strokeWidth: unScale(1)
          })
        );
      } else if (scaleInNumber >= 0.4 && yCoordinate % 5 === 0) {
        gridLinesHorizontal.add(
          new Konva.Line({
            x: fullRect.x1,
            y: fullRect.y1 + i * STEP_SIZE,
            points: [0, 0, xSize, 0],
            stroke: 'rgba(200, 200, 200, 1)',
            strokeWidth: unScale(1)
          })
        );
      }
    }

   

    const drawRulers = () => {
		const trenchHalfWidth = getTrenchTopWidth(trenchData) / 2;
		const trenchHeight = getTrenchHeight(trenchData);

     	const createDepthRuler = (yStart: number, yEnd: number, trenchSide: 'left' | 'right') => {
        	let xPos = trenchHalfWidth * 1000;
        	if(trenchSide === 'left'){
          		xPos *= -1;
          		xPos -= 10; // Add some offset to the left side so it doesn't overlap the trench line.
        	} else {
          		// Add some offset to the right side so it doesn't overlap the trench line.
          		xPos += 10;
        	}
        	const markStart = new Konva.Line({
          		points: [
            		xPos - unScale(5),
            		yStart,
            		xPos + unScale(5),
            		yStart
          		],
          		stroke: 'rgba(0,0,255,0.75)'
        	})
        	const lineObject = new Konva.Line({
          		points: [
            		xPos,
            		yStart,
            		xPos,
            		yEnd
          		],
          		stroke: 'rgba(0,0,255,0.75)',
        	});
        	const markEnd = new Konva.Line({
          		points: [
            		xPos - unScale(5),
            		yEnd,
            		xPos + unScale(5),
            		yEnd
          		],
          		stroke: 'rgba(0,0,255,0.75)'
        	})

        	const labelOffset = 10;
        	const labelPlacementY = (yStart + yEnd) / 2;
       		const labelObject = new Konva.Text({
          		x: xPos + labelOffset,
         		y: labelPlacementY,
          		fontSize: unScale(16),
          		text: `${Math.round(yEnd - yStart)} mm`,
          		fontFamily: 'Poppins',
        	});
        	hintLayer.add(markStart, markEnd, lineObject, labelObject);
      	};
      	const drawFullDepth = () => {
        	createDepthRuler(0, trenchHeight * 1000, 'right');
      	}
       	const drawFillzonesDepth = () => {
        	trenchData.refillZones.forEach((zone, i) => {
				let yStart = 0;
				let yEnd = 0;
				if (i === 0){
					// yStart still 0
					yEnd = zone.depth * 1000;
				}else {
					for(let j = 0; j < i; j++){
						yStart += trenchData.refillZones[j].depth * 1000;
					}
					yEnd = yStart + zone.depth * 1000;
				}

				createDepthRuler(yStart, yEnd, 'left');
        	});
      	}

       	const drawCablezonesDepth = () => {
        	const coverThickness = getCoverThickness(trenchData) * 1000;
        	trenchData.cableZones.forEach((zone, i) => {
          		let yStart = coverThickness;
          		let yEnd = 0;
          		if (i === 0){
            		// yStart still base
            		yEnd = coverThickness + getCableAreaHeight(trenchData.cableZones[i]) * 1000;
				}else {
					// Add previous zones height to yStart
					let yStart = coverThickness - zone.margin * 1000;
					for(let j = 0; j < i; j++){
					yStart += getCableAreaHeight(trenchData.cableZones[j]) * 1000;
					}
					yEnd = yStart + getCableAreaHeight(trenchData.cableZones[i]) * 1000;
				}

				createDepthRuler(yStart, yEnd, 'left');
				});
      	}

		const drawFoundationDepth = () => {
			const coverThickness = getCoverThickness(trenchData) * 1000;
			const cableAreaHeight = trenchData.cableZones.reduce((sum, zone) => sum + getCableAreaHeight(zone), 0) * 1000;
			trenchData.foundationZones.forEach((zone, i) => {
				let yStart = coverThickness + cableAreaHeight;
				let yEnd = 0;
				if (i === 0){
					// yStart still 0
					yEnd = yStart + zone.depth * 1000;
				}else {
					for(let j = 0; j < i; j++){
					yStart += trenchData.foundationZones[j].depth * 1000;
					}
					yEnd = yStart + zone.depth * 1000;
				}
				createDepthRuler(yStart, yEnd, 'left');
			});
		}

		const drawObjectdistances = () => {
			//TODO: Find a good synergy with drawing cable objects, so we don't keep redundant code.
			const cableZone = trenchData.cableZones[0];
			const trenchObjects = cableZone.objects.filter((object) => object.type === 'KABEL_HS' || object.type === 'KABEL_LS' || object.type === 'ROR' || object.type === 'JORD');
			const cableBounds = createCableArea(cableZone);
			// Get the transform width of the cable bounds (-50%) to center the cables in the trench.
			const cableOffset = cableBounds/2;
			const trenchHeight = getTrenchHeight(trenchData);
			const cableDepth = trenchHeight - trenchData.foundationZones.reduce((acc, zone) => acc + zone.depth, 0);
			const tallestItemDiameter = Math.max(...trenchObjects.map(trenchObject => {
				const { diameter } = getObjectDefaultMeasurementsByType(trenchObject.data)
				return diameter;
			}));
		
			// Store the x coordinates of cables to position them correctly. And order them
			let xCoords: number[] = [];
		
			trenchObjects.forEach((trenchObject, j) => {
				const object = trenchObject.data;
				let radiusToMm = 0;
				let xPosition = 0;
		
				const previousObject = trenchObjects[j - 1]?.data;
				let currentGap = 0;
			
				if(isPipe(object)){
					radiusToMm = object.pipeBaseData.diameter / 2;
					currentGap = 100; // Pipe default gap
				} else if (isCable(object)){
					radiusToMm = object.cableConductorData.diameter / 2;
					currentGap = 70; // Cable default gap
				} else if (isEcc(object)){
					radiusToMm = object.cableConductorData.diameter / 2;
					currentGap = 70; // ECC default gap
				}
			
				let prevDiameter = 0;
				let prevGap = 0;
				// Create correct offsets on all objects following the first
				if(j !== 0 && previousObject){
					const {
						diameter,
						gap
					} = getObjectDefaultMeasurementsByType(previousObject);

					prevDiameter = diameter;
					prevGap = gap;
					xPosition = // Returns the start position of the new object to render
						xCoords[j - 1] + // Previous position
						diameter + // Previous size
						Math.max(gap, currentGap); // The biggest offset requirement between previous and current.
				}
				xCoords.push(xPosition);
				if(j === 0) return; // No need to draw distance on the last object.
		
			let prevXOffset = xCoords[j - 1] - cableOffset;

			if(isCable(previousObject)){
				drawHorizontalRuler({
					xStart: prevXOffset + prevDiameter,
					xEnd: prevXOffset + prevDiameter + Math.max(prevGap, currentGap),
					yPos: cableDepth * 1000 - tallestItemDiameter/2,
				})
			} else {
				drawHorizontalRuler({
					xStart: prevXOffset + prevDiameter,
					xEnd: prevXOffset + prevDiameter + Math.max(prevGap, currentGap),
					yPos: cableDepth * 1000 - tallestItemDiameter/2,
				})
			}
			
			});
			if(trenchData.cableZones[0].objects.length){
				// Left
				drawHorizontalRuler({
					xStart: -cableOffset,
					xEnd: -cableOffset - trenchData.horizontalPadding * 1000,
					yPos: cableDepth * 1000 - tallestItemDiameter/2,
				});

				drawVertical({
					xPos: -cableOffset - trenchData.horizontalPadding * 1000,
					yStart: cableDepth * 1000 - getCableAreaHeight(trenchData.cableZones[0]) * 1000,
					yEnd: trenchHeight * 1000,
				});

				// Right
				drawHorizontalRuler({
					xStart: cableOffset,
					xEnd: cableOffset + trenchData.horizontalPadding * 1000,
					yPos: cableDepth * 1000 - tallestItemDiameter/2,
				})

				drawVertical({
					xPos: cableOffset + trenchData.horizontalPadding * 1000,
					yStart: cableDepth * 1000 - getCableAreaHeight(trenchData.cableZones[0]) * 1000,
					yEnd: trenchHeight * 1000,
				});
			}

		}
      
		drawFullDepth();
		drawFillzonesDepth();
		drawCablezonesDepth();
		drawFoundationDepth();
		drawObjectdistances();
    }

    const drawReferencePositions = () => {
		// Even more redundant code.
		// Todo: Refactor this code to run at an appropriate time. May be coupled with drawing objects.
		const { hintLayer, entityLayer } = getLayers();
		entityLayer.getChildren().forEach((gridEntity, i) => {
			let xCoord = gridEntity.x();
			if(gridEntity instanceof Konva.Image){
				xCoord += gridEntity.width() / 2;
			}
			if(gridEntity instanceof Konva.Rect){
				xCoord += gridEntity.width() / 2;
			}
				const yCoord = gridEntity.y() - gridEntity.height() / 2 - unScale(20);
				const circleReference = new Konva.Circle({
					x: xCoord,
					y: yCoord,
					radius: unScale(16),
					fill: 'rgba(200,0,0,1)',
				});
				const circleNumber = new Konva.Text({
					text: i + 1 + '',
					x: xCoord,
					y: yCoord - unScale(8),
					fontSize: unScale(16),
					align: 'center',
					fontFamily: 'Poppins',
					fontStyle: 'Bold',
					fill: 'white',
					offsetX: unScale(4),
				});
				hintLayer.add(circleReference, circleNumber)
			return;
			
		})
		hintLayer.moveToTop();
    }
    
    gridLayer.add(gridLinesHorizontal, gridLinesVertical);
    hintLayer.add(horizontalRulerGroup, verticalRulerGroup);

    if (optionalRulers) drawRulers();
    if (referencePositions) drawReferencePositions();
    
    // Draw the line which represents groundlevel
    gridLayer.add(
		new Konva.Line({
			points: [fullRect.x1, 0, fullRect.x2 - fullRect.x1, 0],
			stroke: 'rgba(0, 0, 0, 1)',
			strokeWidth: 2,
		})
    );
    gridLayer.batchDraw();
  };

  drawGridLines();
};



export {
  rerenderStage,
  unScale,
  clearGrid,
  clearEntities,
  createGrid,
  getLayers,
  getStageObject,
  getViewOptions,
  setViewOptions,
  drawHorizontalRuler
}
